import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { RefreshCw, Loader2, Eye, EyeOff, Trash2 } from 'lucide-react';

export default function AdminTemplates() {
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadPacks(); }, []);

  const loadPacks = async () => {
    try {
      const { data } = await api.get('/templates');
      setPacks(data.packs || data.templates || data || []);
    } catch (err) {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const toggleVisibility = async (packId, hidden) => {
    try {
      await api.put(`/templates/${packId}`, { is_hidden: !hidden });
      toast.success('Visibility updated');
      loadPacks();
    } catch (err) {
      toast.error('Failed to update visibility');
    }
  };

  const deletePack = async (packId, packName) => {
    if (!window.confirm(`Delete template "${packName || 'Untitled'}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/templates/${packId}`);
      toast.success('Template deleted');
      loadPacks();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete template');
    }
  };

  const statusColor = (s) => ({
    draft: 'bg-gray-500/20 text-gray-400',
    reviewed: 'bg-blue-500/20 text-blue-400',
    tested: 'bg-purple-500/20 text-purple-400',
    published: 'bg-green-500/20 text-green-400',
    archived: 'bg-red-500/20 text-red-400',
  }[s] || 'bg-gray-500/20 text-gray-400');

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-[#00a884]" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Templates</h2>
          <p className="text-[#8696a0] text-sm mt-1">Legal and government-service template management</p>
        </div>
        <button onClick={loadPacks} className="p-2 text-[#8696a0] hover:text-white transition"><RefreshCw className="w-4 h-4" /></button>
      </div>

      <div className="grid gap-4">
        {packs.length === 0 ? (
          <div className="text-center py-12 text-[#8696a0]">No templates found</div>
        ) : packs.map(p => (
          <div key={p.id} className="bg-[#111b21] rounded-xl border border-[#2a3942] p-5">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-white font-semibold">{p.title || p.document_type || 'Untitled'}</h3>
                <p className="text-[#8696a0] text-xs mt-0.5">
                  {p.service_family || p.document_type || '—'} · v{p.version || '1'} · {p.language || 'en'}
                  {p.state ? ` · ${p.state}` : ''}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {p.is_hidden !== undefined && (
                  <button onClick={() => toggleVisibility(p.id, p.is_hidden)}
                    className={`p-1.5 rounded-lg ${p.is_hidden ? 'text-red-400 hover:text-red-300' : 'text-[#8696a0] hover:text-white'}`}
                    title={p.is_hidden ? 'Hidden from clients' : 'Visible to clients'}>
                    {p.is_hidden ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                )}
                <button onClick={() => deletePack(p.id, p.title || p.document_type || p.name)}
                  className="p-1.5 rounded-lg text-[#8696a0] hover:text-red-400 transition"
                  title="Delete template">
                  <Trash2 className="w-4 h-4" />
                </button>
                <span className={`px-2 py-1 rounded-full text-xs ${statusColor(p.status)}`}>{p.status}</span>
              </div>
            </div>
            {p.description && <p className="text-[#8696a0] text-sm mt-2">{p.description}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}
